import React, { useState, useEffect } from "react";
import TopBar from "../sidebar/TopBar";
import { useSelector } from "react-redux";

const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
const getGreeting = () => {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
};

// ── LIGHT THEME COLORS ──
const c = {
  bg:           "#F4F6FD",
  surface:      "#FFFFFF",
  surfaceHov:   "#F7F8FC",
  border:       "#E8ECF4",
  text:         "#1A202C",
  textMuted:    "#718096",
  textFaint:    "#A0AEC0",
  accent:       "#4F46E5",
  accentLight:  "#EEF2FF",
  accentBorder: "#C7D2FE",
  scrollbar:    "#CBD5E0",
};

const STATS = [
  { icon: "🚀", label: "Active Projects",  value: "12", color: "#4F46E5", light: "#EEF2FF",               delay: "0.05s" },
  { icon: "✅", label: "Tasks Completed",  value: "48", color: "#059669", light: "#D1FAE5",               delay: "0.10s" },
  { icon: "💬", label: "Open Messages",    value: "5",  color: "#D97706", light: "#FEF3C7",               delay: "0.15s" },
  { icon: "📄", label: "Pending Invoices", value: "3",  color: "#DC2626", light: "#FEE2E2",               delay: "0.20s" },
];

const ACTIVITY = [
  { icon: "📁", text: "New project brief uploaded",       time: "2 min ago",  color: "#4F46E5" },
  { icon: "💬", text: "You received a new message",       time: "14 min ago", color: "#D97706" },
  { icon: "✅", text: 'Task "Design Review" completed',   time: "1 hr ago",   color: "#059669" },
  { icon: "📄", text: "Invoice #0042 sent to billing",    time: "3 hrs ago",  color: "#DC2626" },
  { icon: "🔔", text: "Meeting scheduled for tomorrow",   time: "Yesterday",  color: "#0284C7" },
];

const QUICK = [
  { icon: "➕", label: "New Project",  desc: "Start something new",  color: "#4F46E5", light: "#EEF2FF" },
  { icon: "📨", label: "Send Message", desc: "Contact your team",    color: "#059669", light: "#D1FAE5" },
  { icon: "📋", label: "View Tasks",   desc: "See your to-do list",  color: "#D97706", light: "#FEF3C7" },
  { icon: "🧾", label: "Invoices",     desc: "Track your billing",   color: "#DC2626", light: "#FEE2E2" },
];

const StatCard = ({ icon, label, value, color, light, delay }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: c.surface,
        border: "1px solid " + (hov ? color + "55" : c.border),
        borderRadius: "18px", padding: "22px",
        display: "flex", flexDirection: "column", gap: "14px",
        animation: "fadeUp 0.5s ease both", animationDelay: delay,
        boxShadow: hov ? "0 8px 24px " + color + "18" : "0 1px 4px rgba(0,0,0,0.04)",
        transform: hov ? "translateY(-2px)" : "translateY(0)",
        transition: "all 0.2s", cursor: "default",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ width: 44, height: 44, borderRadius: "12px", background: light, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "20px" }}>{icon}</div>
        <div style={{ width: 8, height: 8, borderRadius: "50%", background: color, animation: "pulse 2s infinite" }} />
      </div>
      <div>
        <div style={{ fontSize: "32px", fontWeight: 800, color: c.text, letterSpacing: "-1px", lineHeight: 1 }}>{value}</div>
        <div style={{ fontSize: "13px", color: c.textMuted, marginTop: "5px", fontWeight: 500 }}>{label}</div>
      </div>
    </div>
  );
};

const QuickBtn = ({ icon, label, desc, color, light }) => {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: hov ? light : c.surfaceHov,
        border: "1px solid " + (hov ? color + "44" : c.border),
        borderRadius: "14px", padding: "14px 16px",
        display: "flex", alignItems: "center", gap: "14px",
        cursor: "pointer", transition: "all 0.18s",
        transform: hov ? "scale(1.01)" : "scale(1)",
        boxShadow: hov ? "0 4px 12px " + color + "15" : "none",
      }}
    >
      <div style={{ width: 40, height: 40, borderRadius: "12px", background: light, display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", flexShrink: 0 }}>{icon}</div>
      <div>
        <div style={{ fontSize: "14px", fontWeight: 700, color: c.text }}>{label}</div>
        <div style={{ fontSize: "12px", color: c.textMuted, marginTop: "2px" }}>{desc}</div>
      </div>
      <div style={{ marginLeft: "auto", fontSize: "16px", color: hov ? color : c.textFaint, transition: "color 0.18s" }}>→</div>
    </div>
  );
};

const ClientDashboard = () => {
  const { userData } = useSelector((store) => store.userReducer);
  const clientName = userData?.name || "User";
  const initials = clientName.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase();
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  const timeStr = time.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true });

  return (
    <div style={{ minHeight: "100vh", background: c.bg, fontFamily: "'DM Sans', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,600;0,9..40,700;0,9..40,800&family=JetBrains+Mono:wght@400;600&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        @keyframes fadeUp  { from { opacity:0; transform:translateY(18px); } to { opacity:1; transform:translateY(0); } }
        @keyframes fadeIn  { from { opacity:0; } to { opacity:1; } }
        @keyframes pulse   { 0%,100%{ opacity:1; } 50%{ opacity:.35; } }
        @keyframes float   { 0%,100%{ transform:translateY(0); } 50%{ transform:translateY(-6px); } }
        ::-webkit-scrollbar { width:5px; }
        ::-webkit-scrollbar-thumb { background:#CBD5E0; border-radius:10px; }
      `}</style>

      <TopBar />

      <div style={{ maxWidth: "1320px", margin: "0 auto", padding: "28px 24px 60px" }}>

        {/* ── WELCOME BANNER ── */}
        <div style={{
          position: "relative", overflow: "hidden",
          background: "linear-gradient(135deg, #EEF2FF 0%, #E0E7FF 50%, #EDE9FE 100%)",
          borderRadius: "24px", padding: "36px 40px",
          marginBottom: "28px",
          border: "1px solid " + c.accentBorder,
          boxShadow: "0 4px 24px rgba(79,70,229,0.08)",
          animation: "fadeUp 0.4s ease both",
        }}>
          {/* Decorative orbs */}
          <div style={{ position: "absolute", top: -50, right: -50, width: 220, height: 220, borderRadius: "50%", background: "rgba(79,70,229,0.06)", filter: "blur(50px)", pointerEvents: "none" }} />
          <div style={{ position: "absolute", bottom: -30, left: "40%", width: 180, height: 180, borderRadius: "50%", background: "rgba(99,102,241,0.05)", filter: "blur(40px)", pointerEvents: "none" }} />

          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: "16px", position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
              {/* Avatar */}
              <div style={{
                width: 60, height: 60, borderRadius: "18px",
                background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "22px", fontWeight: 800, color: "#fff",
                boxShadow: "0 8px 24px rgba(79,70,229,0.3)",
                animation: "float 4s ease-in-out infinite",
                flexShrink: 0,
              }}>{initials}</div>

              <div>
                <div style={{ fontSize: "12px", fontWeight: 700, color: c.accent, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: "4px" }}>
                  {getGreeting()} 👋
                </div>
                <h2 style={{ fontSize: "clamp(22px, 3vw, 30px)", fontWeight: 800, color: c.text, margin: 0, letterSpacing: "-0.5px" }}>
                  Hi, {clientName}!
                </h2>
                <p style={{ fontSize: "13px", color: c.textMuted, marginTop: "4px", fontWeight: 500 }}>
                  Here's what's happening with your account today.
                </p>
              </div>
            </div>

            {/* Time + date pills */}
            <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
              <div style={{
                background: c.surface, border: "1px solid " + c.accentBorder,
                borderRadius: "12px", padding: "8px 16px",
                display: "flex", alignItems: "center", gap: "8px",
                boxShadow: "0 2px 8px rgba(79,70,229,0.08)",
              }}>
                <span style={{ fontSize: "14px" }}>🕐</span>
                <span style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: "15px", fontWeight: 600, color: c.accent, letterSpacing: "0.04em" }}>{timeStr}</span>
              </div>
              <div style={{
                background: "#EFF6FF", border: "1px solid #BAE6FD",
                borderRadius: "12px", padding: "7px 14px",
                fontSize: "12px", fontWeight: 600, color: "#0284C7",
              }}>
                📅 {today}
              </div>
            </div>
          </div>
        </div>

        {/* ── STATS GRID ── */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "16px", marginBottom: "28px" }}>
          {STATS.map((s, i) => <StatCard key={i} {...s} />)}
        </div>

        {/* ── MAIN GRID ── */}
        <div style={{ display: "grid", gridTemplateColumns: "1fr 340px", gap: "20px", alignItems: "start" }}>

          {/* Recent Activity */}
          <div style={{
            background: c.surface, border: "1px solid " + c.border,
            borderRadius: "20px", overflow: "hidden",
            boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
            animation: "fadeUp 0.5s ease both", animationDelay: "0.25s",
          }}>
            <div style={{ padding: "20px 22px 16px", borderBottom: "1px solid " + c.border, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontSize: "15px", fontWeight: 700, color: c.text }}>Recent Activity</div>
              <div style={{ fontSize: "12px", fontWeight: 600, color: c.accent, cursor: "pointer" }}>View all →</div>
            </div>
            <div>
              {ACTIVITY.map((a, i) => (
                <div key={i}
                  style={{
                    display: "flex", alignItems: "center", gap: "14px",
                    padding: "14px 22px",
                    borderBottom: i < ACTIVITY.length - 1 ? "1px solid " + c.border : "none",
                    transition: "background 0.15s", cursor: "default",
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = c.surfaceHov}
                  onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                >
                  <div style={{ width: 38, height: 38, borderRadius: "10px", background: a.color + "15", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px", flexShrink: 0 }}>{a.icon}</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: "13px", fontWeight: 600, color: c.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{a.text}</div>
                    <div style={{ fontSize: "11px", color: c.textFaint, marginTop: "2px" }}>{a.time}</div>
                  </div>
                  <div style={{ width: 6, height: 6, borderRadius: "50%", background: a.color, flexShrink: 0 }} />
                </div>
              ))}
            </div>
          </div>

          {/* Right column */}
          <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

            {/* Quick Actions */}
            <div style={{
              background: c.surface, border: "1px solid " + c.border,
              borderRadius: "20px", overflow: "hidden",
              boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
              animation: "fadeUp 0.5s ease both", animationDelay: "0.3s",
            }}>
              <div style={{ padding: "20px 22px 14px", borderBottom: "1px solid " + c.border }}>
                <div style={{ fontSize: "15px", fontWeight: 700, color: c.text }}>Quick Actions</div>
              </div>
              <div style={{ padding: "12px 12px", display: "flex", flexDirection: "column", gap: "6px" }}>
                {QUICK.map((q, i) => <QuickBtn key={i} {...q} />)}
              </div>
            </div>

            {/* Profile summary */}
            <div style={{
              background: "linear-gradient(135deg, #EEF2FF, #E0E7FF)",
              border: "1px solid " + c.accentBorder,
              borderRadius: "20px", padding: "22px",
              boxShadow: "0 2px 12px rgba(79,70,229,0.08)",
              animation: "fadeUp 0.5s ease both", animationDelay: "0.35s",
              textAlign: "center",
            }}>
              <div style={{
                width: 52, height: 52, borderRadius: "16px",
                background: "linear-gradient(135deg, #4F46E5, #7C3AED)",
                display: "flex", alignItems: "center", justifyContent: "center",
                fontSize: "20px", fontWeight: 800, color: "#fff",
                margin: "0 auto 12px",
                boxShadow: "0 4px 16px rgba(79,70,229,0.3)",
              }}>{initials}</div>
              <div style={{ fontSize: "16px", fontWeight: 700, color: c.text, marginBottom: "3px" }}>{clientName}</div>
              <div style={{ fontSize: "12px", color: c.textMuted, marginBottom: "16px" }}>{userData?.email || "client@aihub.com"}</div>
              <div style={{ display: "flex", gap: "8px" }}>
                <div style={{ flex: 1, background: c.accentLight, border: "1px solid " + c.accentBorder, borderRadius: "10px", padding: "10px 0", textAlign: "center" }}>
                  <div style={{ fontSize: "18px", fontWeight: 800, color: c.accent }}>12</div>
                  <div style={{ fontSize: "11px", color: c.textMuted, marginTop: "2px" }}>Projects</div>
                </div>
                <div style={{ flex: 1, background: "#D1FAE5", border: "1px solid #A7F3D0", borderRadius: "10px", padding: "10px 0", textAlign: "center" }}>
                  <div style={{ fontSize: "18px", fontWeight: 800, color: "#059669" }}>48</div>
                  <div style={{ fontSize: "11px", color: c.textMuted, marginTop: "2px" }}>Tasks</div>
                </div>
                <div style={{ flex: 1, background: "#FEF3C7", border: "1px solid #FDE68A", borderRadius: "10px", padding: "10px 0", textAlign: "center" }}>
                  <div style={{ fontSize: "18px", fontWeight: 800, color: "#D97706" }}>5</div>
                  <div style={{ fontSize: "11px", color: c.textMuted, marginTop: "2px" }}>Messages</div>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientDashboard;